/*
  Shared Wishlist service.
  This file owns all browser persistence so cards, product pages, and the
  Wishlist page do not each invent their own localStorage logic.
*/
(function () {
  var BODY = document.body;
  var STORAGE_KEY = BODY && BODY.getAttribute('data-wishlist-storage-key')
    ? BODY.getAttribute('data-wishlist-storage-key')
    : 'tcghive:wishlist:v1';
  var STORAGE_VERSION = 1;
  var changeSubscribers = [];
  var liveRegionId = 'TcghiveWishlistLiveRegion';

  function getRootLanguage() {
    return document.documentElement && document.documentElement.lang
      ? document.documentElement.lang
      : 'de';
  }

  function getText(key, fallback) {
    if (!BODY) {
      return fallback;
    }

    return BODY.getAttribute(key) || fallback;
  }

  function replaceTemplateTokens(template, replacements) {
    var output = String(template || '');

    Object.keys(replacements).forEach(function (token) {
      output = output.split('[' + token + ']').join(String(replacements[token]));
    });

    return output;
  }

  /*
    The global live region gives screen-reader users feedback whenever a saved
    state changes without forcing us to duplicate aria-live markup on every page.
  */
  function ensureLiveRegion() {
    var existingRegion = document.getElementById(liveRegionId);
    var liveRegion;

    if (existingRegion) {
      return existingRegion;
    }

    liveRegion = document.createElement('div');
    liveRegion.id = liveRegionId;
    liveRegion.className = 'visually-hidden';
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');

    if (document.body) {
      document.body.appendChild(liveRegion);
    }

    return liveRegion;
  }

  function announce(message) {
    var liveRegion;

    if (!message) {
      return;
    }

    liveRegion = ensureLiveRegion();

    if (!liveRegion) {
      return;
    }

    liveRegion.textContent = '';

    window.setTimeout(function () {
      liveRegion.textContent = message;
    }, 20);
  }

  function safeNumber(value) {
    var parsed = Number(value);

    if (!Number.isFinite(parsed)) {
      return 0;
    }

    return parsed;
  }

  function readPositiveInteger(value) {
    var parsed = Number(value);

    if (!Number.isFinite(parsed) || parsed <= 0) {
      return null;
    }

    return Math.floor(parsed);
  }

  function safeString(value) {
    if (value === null || value === undefined) {
      return '';
    }

    return String(value).trim();
  }

  function normalizeKind(value) {
    var normalized = safeString(value).toLowerCase();

    if (normalized === 'sealed') {
      return 'sealed';
    }

    return 'single';
  }

  function normalizeSelectedOptions(rawItem) {
    var selectedOptions = {};
    var rawSelectedOptions = rawItem && rawItem.selectedOptions ? rawItem.selectedOptions : {};

    if (safeString(rawSelectedOptions.Language) !== '') {
      selectedOptions.Language = safeString(rawSelectedOptions.Language);
    } else if (safeString(rawItem.language) !== '') {
      selectedOptions.Language = safeString(rawItem.language);
    }

    if (safeString(rawSelectedOptions.Condition) !== '') {
      selectedOptions.Condition = safeString(rawSelectedOptions.Condition);
    } else if (safeString(rawItem.condition) !== '') {
      selectedOptions.Condition = safeString(rawItem.condition);
    }

    if (safeString(rawSelectedOptions.Finish) !== '') {
      selectedOptions.Finish = safeString(rawSelectedOptions.Finish);
    } else if (safeString(rawItem.finish) !== '') {
      selectedOptions.Finish = safeString(rawItem.finish);
    }

    if (safeString(rawSelectedOptions.Type) !== '') {
      selectedOptions.Type = safeString(rawSelectedOptions.Type);
    } else if (safeString(rawItem.typeLabel) !== '') {
      selectedOptions.Type = safeString(rawItem.typeLabel);
    }

    return selectedOptions;
  }

  /*
    Every saved Wishlist item is identified by the exact Shopify variant ID.
    This keeps DE/LP/Foil separate from EN/NM/Non-Foil for the same product.
  */
  function normalizeItem(rawItem) {
    var variantId = safeString(rawItem && rawItem.variantId);
    var handle = safeString(rawItem && rawItem.handle);
    var cartMaxQuantity = readPositiveInteger(rawItem && rawItem.cartMaxQuantity);
    var normalizedItem;

    if (variantId === '' || handle === '') {
      return null;
    }

    normalizedItem = {
      key: 'variant:' + variantId,
      productId: safeString(rawItem.productId),
      variantId: variantId,
      handle: handle,
      kind: normalizeKind(rawItem.kind),
      title: safeString(rawItem.title),
      productUrl: safeString(rawItem.productUrl),
      image: safeString(rawItem.image),
      setName: safeString(rawItem.setName),
      rarity: safeString(rawItem.rarity),
      language: safeString(rawItem.language),
      condition: safeString(rawItem.condition),
      finish: safeString(rawItem.finish),
      previewText: safeString(rawItem.previewText),
      savedAt: safeNumber(rawItem.savedAt) || Date.now(),
      selectedOptions: normalizeSelectedOptions(rawItem)
    };

    if (cartMaxQuantity !== null) {
      normalizedItem.cartMaxQuantity = cartMaxQuantity;
    }

    return normalizedItem;
  }

  function dedupeItems(items) {
    var seenKeys = {};
    var dedupedItems = [];

    items.forEach(function (rawItem) {
      var normalizedItem = normalizeItem(rawItem);

      if (!normalizedItem || seenKeys[normalizedItem.key]) {
        return;
      }

      seenKeys[normalizedItem.key] = true;
      dedupedItems.push(normalizedItem);
    });

    dedupedItems.sort(function (leftItem, rightItem) {
      return safeNumber(rightItem.savedAt) - safeNumber(leftItem.savedAt);
    });

    return dedupedItems;
  }

  function defaultState() {
    return {
      version: STORAGE_VERSION,
      items: []
    };
  }

  function parseStoredValue(rawValue) {
    var parsedValue;
    var parsedItems;

    if (!rawValue) {
      return defaultState();
    }

    try {
      parsedValue = JSON.parse(rawValue);
    } catch (error) {
      return defaultState();
    }

    if (!parsedValue || typeof parsedValue !== 'object') {
      return defaultState();
    }

    parsedItems = Array.isArray(parsedValue.items) ? parsedValue.items : [];

    return {
      version: STORAGE_VERSION,
      items: dedupeItems(parsedItems)
    };
  }

  function readState() {
    try {
      return parseStoredValue(window.localStorage.getItem(STORAGE_KEY));
    } catch (error) {
      return defaultState();
    }
  }

  function writeState(nextItems) {
    var normalizedState = {
      version: STORAGE_VERSION,
      items: dedupeItems(nextItems)
    };

    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalizedState));
    } catch (error) {
      /*
        Storage writes can fail in private browsing or quota scenarios.
        The storefront should keep functioning even if persistence is blocked.
      */
    }

    return normalizedState;
  }

  function getItems() {
    return readState().items.slice();
  }

  function findItemByVariantId(variantId) {
    var normalizedVariantId = safeString(variantId);

    if (normalizedVariantId === '') {
      return null;
    }

    return getItems().find(function (savedItem) {
      return safeString(savedItem.variantId) === normalizedVariantId;
    }) || null;
  }

  function has(variantId) {
    return findItemByVariantId(variantId) !== null;
  }

  function updateHeaderWishlistLinks(totalCount, options) {
    var shouldAnimate = Boolean(options && options.animateHeader);

    document.querySelectorAll('[data-wishlist-header-link]').forEach(function (link) {
      var baseLabel = link.getAttribute('data-wishlist-label') || link.getAttribute('aria-label') || '';

      link.classList.toggle('has-items', totalCount > 0);
      link.setAttribute('aria-label', totalCount > 0 ? baseLabel + ' (' + totalCount + ')' : baseLabel);

      if (!shouldAnimate) {
        return;
      }

      link.classList.remove('is-wishlist-pulsing');

      /*
        Removing and re-adding the class lets sequential Wishlist additions play
        a fresh, short pulse without adding extra per-button click listeners.
      */
      window.requestAnimationFrame(function () {
        link.classList.add('is-wishlist-pulsing');
      });

      window.setTimeout(function () {
        link.classList.remove('is-wishlist-pulsing');
      }, 380);
    });
  }

  function updateCountHooks(options) {
    var totalCount = getItems().length;

    document.querySelectorAll('[data-wishlist-count]').forEach(function (countElement) {
      countElement.textContent = String(totalCount);
      countElement.hidden = totalCount === 0;
    });

    updateHeaderWishlistLinks(totalCount, options);
  }

  function updateToggleButton(button) {
    var variantId = safeString(button.getAttribute('data-wishlist-variant-id'));
    var isSaved = variantId !== '' && has(variantId);
    var buttonLabel = isSaved
      ? getText('data-wishlist-remove-label', 'Von der Merkliste entfernen')
      : getText('data-wishlist-add-label', 'Zur Merkliste hinzufügen');

    button.setAttribute('aria-pressed', isSaved ? 'true' : 'false');
    button.setAttribute('aria-label', buttonLabel);
    button.classList.toggle('is-active', isSaved);

    if (button.hasAttribute('data-wishlist-toggle-disabled')) {
      button.classList.remove('is-active');
      button.setAttribute('aria-pressed', 'false');
    }
  }

  function syncButtons(scope, options) {
    var root = scope || document;

    root.querySelectorAll('[data-wishlist-toggle]').forEach(function (button) {
      updateToggleButton(button);
    });

    updateCountHooks(options);
  }

  function emitChange(detail) {
    var nextDetail = detail || {};

    nextDetail.items = getItems();
    nextDetail.count = nextDetail.items.length;

    changeSubscribers.slice().forEach(function (subscriber) {
      try {
        subscriber(nextDetail);
      } catch (error) {
        /*
          One subscriber should never block the rest of the storefront updates.
        */
      }
    });

    document.dispatchEvent(
      new CustomEvent('tcghive:wishlist-changed', {
        detail: nextDetail
      })
    );

    /*
      Keep the older event name in place as a low-risk bridge so any remaining
      legacy listeners continue to update during the migration.
    */
    document.dispatchEvent(
      new CustomEvent('tcghive:wishlist:changed', {
        detail: nextDetail
      })
    );
  }

  function add(rawItem, options) {
    var normalizedItem = normalizeItem(rawItem);
    var storedItems = getItems();
    var nextItems = [];
    var announceAdd = !options || options.announce !== false;
    var addedTemplate = getText('data-wishlist-added-template', '[product] wurde zur Merkliste hinzugefügt.');

    if (!normalizedItem) {
      return null;
    }

    nextItems.push(normalizedItem);

    storedItems.forEach(function (storedItem) {
      if (storedItem.key !== normalizedItem.key) {
        nextItems.push(storedItem);
      }
    });

    writeState(nextItems);
    syncButtons(document, {
      animateHeader: true
    });
    emitChange({
      action: 'added',
      variantId: normalizedItem.variantId,
      item: normalizedItem,
      source: options && options.source ? options.source : 'manual'
    });

    if (announceAdd) {
      announce(
        replaceTemplateTokens(addedTemplate, {
          product: normalizedItem.title || normalizedItem.handle
        })
      );
    }

    return normalizedItem;
  }

  function remove(target, options) {
    var normalizedVariantId = safeString(
      typeof target === 'object' && target !== null ? target.variantId : target
    );
    var storedItems = getItems();
    var removedItem = null;
    var nextItems;
    var announceRemoval = !options || options.announce !== false;
    var removedTemplate = getText('data-wishlist-removed-template', '[product] wurde von der Merkliste entfernt.');

    if (normalizedVariantId === '') {
      return null;
    }

    nextItems = storedItems.filter(function (storedItem) {
      var keepItem = safeString(storedItem.variantId) !== normalizedVariantId;

      if (!keepItem && !removedItem) {
        removedItem = storedItem;
      }

      return keepItem;
    });

    writeState(nextItems);
    syncButtons(document);
    emitChange({
      action: 'removed',
      variantId: normalizedVariantId,
      item: removedItem,
      source: options && options.source ? options.source : 'manual'
    });

    if (announceRemoval && removedItem) {
      announce(
        replaceTemplateTokens(removedTemplate, {
          product: removedItem.title || removedItem.handle
        })
      );
    }

    return removedItem;
  }

  function clear(options) {
    writeState([]);
    syncButtons(document);
    emitChange({
      action: 'cleared',
      source: options && options.source ? options.source : 'manual'
    });
  }

  function toggle(rawItem, options) {
    var normalizedItem = normalizeItem(rawItem);

    if (!normalizedItem) {
      return {
        saved: false,
        item: null
      };
    }

    if (has(normalizedItem.variantId)) {
      remove(normalizedItem.variantId, options);

      return {
        saved: false,
        item: normalizedItem
      };
    }

    add(normalizedItem, options);

    return {
      saved: true,
      item: normalizedItem
    };
  }

  function subscribe(callback) {
    if (typeof callback !== 'function') {
      return function () {};
    }

    changeSubscribers.push(callback);

    return function unsubscribe() {
      changeSubscribers = changeSubscribers.filter(function (registeredCallback) {
        return registeredCallback !== callback;
      });
    };
  }

  function buildItemFromButton(button) {
    return normalizeItem({
      productId: button.getAttribute('data-wishlist-product-id'),
      variantId: button.getAttribute('data-wishlist-variant-id'),
      handle: button.getAttribute('data-wishlist-handle'),
      kind: button.getAttribute('data-wishlist-kind'),
      title: button.getAttribute('data-wishlist-title'),
      productUrl: button.getAttribute('data-wishlist-product-url'),
      image: button.getAttribute('data-wishlist-image'),
      setName: button.getAttribute('data-wishlist-set-name'),
      rarity: button.getAttribute('data-wishlist-rarity'),
      language: button.getAttribute('data-wishlist-language'),
      condition: button.getAttribute('data-wishlist-condition'),
      finish: button.getAttribute('data-wishlist-finish'),
      previewText: button.getAttribute('data-wishlist-preview-text'),
      cartMaxQuantity: button.getAttribute('data-wishlist-cart-max-quantity'),
      selectedOptions: {
        Language: button.getAttribute('data-wishlist-language'),
        Condition: button.getAttribute('data-wishlist-condition'),
        Finish: button.getAttribute('data-wishlist-finish'),
        Type: button.getAttribute('data-wishlist-type-label')
      },
      savedAt: Date.now()
    });
  }

  function handleToggleClick(event) {
    var button = event.target.closest('[data-wishlist-toggle]');
    var wishlistItem;

    if (!button || button.hasAttribute('disabled') || button.getAttribute('data-wishlist-toggle-disabled') === 'true') {
      return;
    }

    wishlistItem = buildItemFromButton(button);

    if (!wishlistItem) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    toggle(wishlistItem, {
      source: 'toggle-button'
    });
  }

  function handleStorageChange(event) {
    if (event.key !== STORAGE_KEY) {
      return;
    }

    syncButtons(document);
    emitChange({
      action: 'storage',
      source: 'storage'
    });
  }

  window.TCGHive = window.TCGHive || {};
  window.TCGHive.Wishlist = {
    storageKey: STORAGE_KEY,
    getItems: getItems,
    has: has,
    add: add,
    remove: remove,
    toggle: toggle,
    clear: clear,
    count: function () {
      return getItems().length;
    },
    subscribe: subscribe,
    refreshUI: syncButtons,
    syncButtons: syncButtons
  };

  document.addEventListener('click', handleToggleClick);
  document.addEventListener('DOMContentLoaded', function () {
    ensureLiveRegion();
    syncButtons(document);
  });
  document.addEventListener('shopify:section:load', function (event) {
    syncButtons(event.target || document);
  });
  window.addEventListener('storage', handleStorageChange);
})();
